import { Marker, Popup, Tooltip, Circle } from 'react-leaflet'
import L from 'leaflet'
import { useState } from 'react'
import type { Runner, LocationPoint } from '../../types/index'

interface VanMarkerProps {
  vanNumber: 1 | 2
  runners: Runner[]
  locations: Record<number, LocationPoint>
  currentRunnerId?: number
}

export function VanMarker({
  vanNumber,
  runners,
  locations,
  currentRunnerId,
}: VanMarkerProps) {
  const STALE_MS = 3 * 60 * 1000 // 3 minutes
  const [now, setNow] = useState(Date.now())

  const waitingRunners = runners.filter(r => r.id !== currentRunnerId)
  const waitingLocations = waitingRunners
    .map(r => locations[r.id])
    .filter((loc): loc is LocationPoint => !!loc)

  if (waitingLocations.length === 0) {
    return null
  }

  const runnerStatus = waitingRunners.map(runner => {
    const loc = locations[runner.id]
    if (!loc) return { runner, loc: null, fresh: false }
    const age = now - new Date(loc.timestamp).getTime()
    return { runner, loc, fresh: age < STALE_MS }
  })

  const latestTimestamp = waitingLocations.reduce((latest, loc) =>
    loc.timestamp > latest ? loc.timestamp : latest, waitingLocations[0].timestamp)
  const lastRefreshed = new Date(latestTimestamp).toLocaleTimeString('en-US', {
    timeZone: 'America/Los_Angeles', hour: '2-digit', minute: '2-digit', second: '2-digit',
  })

  const centerLat = waitingLocations.reduce((sum, loc) => sum + loc.latitude, 0) / waitingLocations.length
  const centerLng = waitingLocations.reduce((sum, loc) => sum + loc.longitude, 0) / waitingLocations.length

  const distances = waitingLocations.map(loc => {
    const latDiff = loc.latitude - centerLat
    const lngDiff = loc.longitude - centerLng
    return Math.sqrt(latDiff * latDiff + lngDiff * lngDiff) * 111000
  })
  const maxDistance = Math.max(...distances)

  const iconColor = vanNumber === 1 ? '#8b5cf6' : '#ec4899'
  const iconHtml = `
    <div style="
      background-color: ${iconColor};
      border: 3px solid white;
      border-radius: 50%;
      width: 40px;
      height: 40px;
      display: flex;
      align-items: center;
      justify-content: center;
      font-weight: bold;
      color: white;
      font-size: 16px;
      box-shadow: 0 2px 8px rgba(0,0,0,0.3);
    ">
      V${vanNumber}
    </div>
  `

  const icon = L.divIcon({
    html: iconHtml,
    iconSize: [40, 40],
    className: 'custom-div-icon',
  })

  return (
    <>
      <Marker position={[centerLat, centerLng]} icon={icon}>
        <Tooltip sticky direction="top" offset={[0, -20]}>
          <div className="font-semibold">Van {vanNumber} ({waitingRunners.length} runners)</div>
        </Tooltip>
        <Popup eventHandlers={{ add: () => setNow(Date.now()) }}>
          <div className="text-sm">
            <p className="font-bold mb-1">Van {vanNumber}</p>
            <div className="space-y-1 mb-2">
              {runnerStatus.map(({ runner, fresh, loc }) => (
                <div key={runner.id} className="text-xs flex items-center gap-1.5">
                  {loc && fresh
                    ? <span style={{ color: '#22c55e' }}>●</span>
                    : <span style={{ color: '#d1d5db' }}>●</span>
                  }
                  #{runner.runnerNumber} {runner.name}
                  {!loc && <span className="text-gray-400">(no GPS)</span>}
                  {loc && !fresh && <span className="text-gray-400">(stale)</span>}
                </div>
              ))}
            </div>
            <p className="text-xs text-gray-400">Last refreshed: {lastRefreshed} PT</p>
          </div>
        </Popup>
      </Marker>
      <Circle
        center={[centerLat, centerLng]}
        radius={Math.max(maxDistance * 1.2, 100)}
        fillColor={iconColor}
        fillOpacity={0.05}
        color={iconColor}
        weight={2}
        dashArray="5, 5"
        opacity={0.4}
      />
    </>
  )
}
